import { Album, AlbumVisibility, FriendGroupSummary, VisibilityContact } from '../photoService';
import {
  apiRequest,
  apiRequestWithETag,
  apiPost,
  apiPatch,
  apiDelete,
  safeApiCall,
  buildUrl,
  mapApiResponse,
  ApiResponse,
} from './client';

// API response interfaces
interface AlbumApiResponse {
  id: string;
  name: string;
  photoCount: number;
  thumbnailUrl: string;
  mediumUrl: string;
  creationDate: string;
  position: number;
  oldestPhotoDate?: string;
  newestPhotoDate?: string;
  visibility: AlbumVisibility;
  description?: string;
  visibilityGroups?: FriendGroupSummary[];
  visibilityContacts?: VisibilityContact[];
  owner?: {
    id: string;
    displayName: string;
  };
}

// Album creation interfaces
export interface CreateAlbumRequest {
  name: string;
  photoIds?: string[];
  visibility: AlbumVisibility;
  description?: string;
  groupIds?: string[];
  contactIds?: string[];
}

export interface UpdateAlbumRequest {
  name: string;
  description?: string;
  visibility?: AlbumVisibility;
  groupIds?: string[];
  contactIds?: string[];
}

export interface PositionUpdate {
  albumId: string;
  position: number;
}

export interface VisibilityUpdateRequest {
  albumIds: string[];
  visibility: AlbumVisibility;
  groupIds?: string[];
  contactIds?: string[];
}

// Paginated result interface matching backend PaginatedResult<Album>
export interface PaginatedAlbumResult {
  items: Album[];
  totalCount: number;
  groups?: { key: string; label: string; count: number }[];
}

// Parameters for fetching albums with filtering/pagination
export interface FetchAlbumsParams {
  orderBy?: string;
  orderDirection?: string;
  search?: string;
  skip?: number;
  take?: number;
  dateFrom?: string;
  dateTo?: string;
  groupBy?: string;
}

// Extended params that include visibility (for user's own albums)
export interface FetchUserAlbumsParams extends FetchAlbumsParams {
  visibility?: string[];  // e.g. ['AllFriends', 'Private']
}

/**
 * Helper function to transform API album response to Album interface
 */
const transformAlbumResponse = (album: AlbumApiResponse): Album => ({
  id: album.id,
  name: album.name,
  photoCount: album.photoCount,
  thumbnailUrl: album.thumbnailUrl,
  mediumUrl: album.mediumUrl,
  creationDate: new Date(album.creationDate),
  position: album.position,
  oldestPhotoDate: album.oldestPhotoDate ? new Date(album.oldestPhotoDate) : undefined,
  newestPhotoDate: album.newestPhotoDate ? new Date(album.newestPhotoDate) : undefined,
  visibility: album.visibility || 'Private',
  description: album.description,
  visibilityGroups: album.visibilityGroups,
  visibilityContacts: album.visibilityContacts,
  owner: album.owner ? {
    id: album.owner.id,
    displayName: album.owner.displayName
  } : undefined
});

/**
 * Fetches albums from the API (all public albums + user's own albums) with pagination
 */
export const fetchAlbumsFromApi = (params: FetchAlbumsParams = {}): Promise<PaginatedAlbumResult> =>
  safeApiCall(async () => {
    const url = buildUrl('/api/albums', {
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
      search: params.search,
      skip: params.skip,
      take: params.take,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      groupBy: params.groupBy,
    });

    const result = await apiRequest<{ items: AlbumApiResponse[]; totalCount: number; groups?: { key: string; label: string; count: number }[] }>(url);
    return {
      items: result.items.map(transformAlbumResponse),
      totalCount: result.totalCount,
      groups: result.groups
    };
  }, "Error fetching albums from API");

/**
 * Fetches albums with ETag-based caching support. Returns the raw `ApiResponse`
 * envelope so callers (typically `useCachedQuery`) can handle 304 responses.
 */
export const fetchAlbumsFromApiWithETag = async (
  params: FetchAlbumsParams = {},
  etag?: string | null
): Promise<ApiResponse<PaginatedAlbumResult>> => {
  const url = buildUrl('/api/albums', {
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    search: params.search,
    skip: params.skip,
    take: params.take,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    groupBy: params.groupBy,
  });

  const response = await apiRequestWithETag<{
    items: AlbumApiResponse[];
    totalCount: number;
    groups?: { key: string; label: string; count: number }[];
  }>(url, etag);

  return mapApiResponse(response, (raw) => ({
    items: raw.items.map(transformAlbumResponse),
    totalCount: raw.totalCount,
    groups: raw.groups,
  }));
};

/**
 * Fetches user's own albums from the API with pagination
 */
export const fetchUserAlbumsFromApi = (userId: string, params: FetchUserAlbumsParams = {}): Promise<PaginatedAlbumResult> =>
  safeApiCall(async () => {
    const url = buildUrl(`/api/user/${encodeURIComponent(userId)}/albums`, {
      orderBy: params.orderBy,
      orderDirection: params.orderDirection,
      search: params.search,
      skip: params.skip,
      take: params.take,
      visibility: params.visibility,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
      groupBy: params.groupBy,
    });

    const result = await apiRequest<{ items: AlbumApiResponse[]; totalCount: number; groups?: { key: string; label: string; count: number }[] }>(url);
    return {
      items: result.items.map(transformAlbumResponse),
      totalCount: result.totalCount,
      groups: result.groups
    };
  }, "Error fetching user albums from API");

/**
 * ETag-aware variant of `fetchUserAlbumsFromApi`.
 */
export const fetchUserAlbumsFromApiWithETag = async (
  userId: string,
  params: FetchUserAlbumsParams = {},
  etag?: string | null
): Promise<ApiResponse<PaginatedAlbumResult>> => {
  const url = buildUrl(`/api/user/${encodeURIComponent(userId)}/albums`, {
    orderBy: params.orderBy,
    orderDirection: params.orderDirection,
    search: params.search,
    skip: params.skip,
    take: params.take,
    visibility: params.visibility,
    dateFrom: params.dateFrom,
    dateTo: params.dateTo,
    groupBy: params.groupBy,
  });

  const response = await apiRequestWithETag<{
    items: AlbumApiResponse[];
    totalCount: number;
    groups?: { key: string; label: string; count: number }[];
  }>(url, etag);

  return mapApiResponse(response, (raw) => ({
    items: raw.items.map(transformAlbumResponse),
    totalCount: raw.totalCount,
    groups: raw.groups,
  }));
};

/**
 * Fetches a single album by ID
 */
export const fetchAlbumById = (albumId: string): Promise<Album> =>
  safeApiCall(async () => {
    const url = buildUrl(`/api/albums/${encodeURIComponent(albumId)}`);
    const album = await apiRequest<AlbumApiResponse>(url);
    return transformAlbumResponse(album);
  }, "Error fetching album from API");

/**
 * ETag-aware variant of `fetchAlbumById`.
 */
export const fetchAlbumByIdWithETag = async (
  albumId: string,
  etag?: string | null
): Promise<ApiResponse<Album>> => {
  const url = buildUrl(`/api/albums/${encodeURIComponent(albumId)}`);
  const response = await apiRequestWithETag<AlbumApiResponse>(url, etag);
  return mapApiResponse(response, transformAlbumResponse);
};

/**
 * Creates a new album
 */
export const createAlbumViaApi = (albumName: string, visibility: AlbumVisibility, photoIds?: string[], description?: string, groupIds?: string[], contactIds?: string[]): Promise<Album> =>
  safeApiCall(async () => {
    const requestBody: CreateAlbumRequest = {
      name: albumName,
      visibility,
      ...(photoIds && photoIds.length > 0 && { photoIds }),
      ...(description && { description }),
      ...(groupIds && groupIds.length > 0 && { groupIds }),
      ...(contactIds && contactIds.length > 0 && { contactIds })
    };

    const response = await apiPost<AlbumApiResponse>(buildUrl('/api/albums'), requestBody);
    return transformAlbumResponse(response);
  }, "Error creating album via API");

/**
 * Updates an album
 */
export const updateAlbumViaApi = (albumId: string, name: string, description?: string): Promise<Album> =>
  safeApiCall(async () => {
    const response = await apiPatch<AlbumApiResponse>(buildUrl(`/api/albums/${encodeURIComponent(albumId)}`), { name, description });
    return transformAlbumResponse(response);
  }, "Error updating album via API");

/**
 * Deletes an album
 */
export const deleteAlbumViaApi = (albumId: string): Promise<void> =>
  safeApiCall(
    () => apiDelete(buildUrl(`/api/albums/${encodeURIComponent(albumId)}`)),
    "Error deleting album via API"
  );

/**
 * Updates album positions
 */
export const updateAlbumPositions = async (positionUpdates: PositionUpdate[]): Promise<void> => {
  await apiPost<void>(buildUrl('/api/albums/positions'), positionUpdates);
};

/**
 * Updates visibility for multiple albums
 */
export const updateAlbumsVisibilityViaApi = (albumIds: string[], visibility: AlbumVisibility, groupIds?: string[], contactIds?: string[]): Promise<void> =>
  safeApiCall(
    () => apiPatch<void>(buildUrl('/api/albums/visibility'), { albumIds, visibility, ...(groupIds && { groupIds }), ...(contactIds && { contactIds }) }),
    "Error updating albums visibility via API"
  );

/**
 * Updates visibility for all user's albums
 */
export const updateAllAlbumsVisibilityViaApi = (visibility: AlbumVisibility, groupIds?: string[], contactIds?: string[]): Promise<void> =>
  safeApiCall(
    () => apiPatch<void>(buildUrl('/api/albums/visibility-all'), { visibility, ...(groupIds && { groupIds }), ...(contactIds && { contactIds }) }),
    "Error updating all albums visibility via API"
  );